import { useCreateManualPayoutBank } from "@entities/campaigns/lib/hooks/useCreateManualPayoutBank";
import { useCreatePayout } from "@entities/campaigns/lib/hooks/useCreatePayout";
import { ToastService } from "@shared/lib/services";
import { AmountType, IInvoice } from "@shared/lib/types/briefCampains";
import { Button } from "@shared/ui/Button";
import { Flex, Input, InputNumber, Select, Space } from "antd";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

const REMARKS_MAX_LENGTH = 30;
const isRemarksValid = (value: string) =>
  value.length <= REMARKS_MAX_LENGTH && /^[a-zA-Z0-9 ]*$/.test(value);

const getRemarksWarning = (value: string) => {
  if (value.length > REMARKS_MAX_LENGTH) {
    return "Remarks must be at most 30 characters";
  }
  if (!/^[a-zA-Z0-9 ]*$/.test(value)) {
    return "Only alphanumeric characters and spaces are allowed";
  }
  return null;
};

export function Payment({
  campaignInfluencerId,
  fullAmount,
  onClose,
  type,
  primaryContactText,
  isPrimaryEmail,
  invoices,
  campaignName = "",
}: {
  campaignInfluencerId: string;
  fullAmount: { base: number; gst: number };
  type: "full" | "partial" | "manual";
  onClose: () => void;
  primaryContactText: string;
  isPrimaryEmail: boolean;
  invoices: IInvoice[];
  campaignName?: string;
}) {
  const [amountType, setAmountType] = useState<AmountType>(AmountType.Base);
  const [partialAmount, setpartialAmount] = useState<number>(0);
  const [remarks, setRemarks] = useState<string>(campaignName);
  const { createPayout, isCreated, isCreating } =
    useCreatePayout(campaignInfluencerId);
  const {
    createManualPayoutBank,
    isCreated: isCreatedManualPayoutBank,
    isCreating: isCreatingManualPayoutBank,
  } = useCreateManualPayoutBank(campaignInfluencerId);
  const [linkedInvoiceId, setLinkedInvoiceId] = useState<string>("");

  const fullAmountByType =
    amountType === AmountType.Base ? fullAmount.base : fullAmount.gst;

  const amount = type === "full" ? fullAmountByType : partialAmount;
  const exceedsMaxPayable = type !== "full" && partialAmount > fullAmountByType;
  const remarksWarning = type !== "full" ? getRemarksWarning(remarks) : null;
  const hasInvalidRemarks = Boolean(remarksWarning);

  useEffect(() => {
    if (isCreated || isCreatedManualPayoutBank) {
      ToastService.success("Payout created successfully");
      onClose();
    }
  }, [isCreated, isCreatedManualPayoutBank]);

  useEffect(() => {
    if (!campaignName) return;
    setRemarks((prev) => (prev.trim() ? prev : campaignName));
  }, [campaignName]);

  const handleSubmit = () => {
    if (type === "full") {
      createPayout({
        campaignInfluencerId,
        amount: fullAmountByType,
        remarks: "",
      });
      return;
    }

    if (!isRemarksValid(remarks)) {
      ToastService.error(
        getRemarksWarning(remarks) ??
          "Remarks must be at most 30 alphanumeric characters (spaces allowed)"
      );
      return;
    }

    if (partialAmount <= 0) {
      ToastService.error("Please enter a payout amount greater than 0");
      return;
    }

    if (partialAmount > fullAmountByType) {
      ToastService.error(
        `Payout amount cannot exceed the max payable of ₹${fullAmountByType.toFixed(2)}`
      );
      return;
    }

    if (type === "partial") {
      createPayout({
        campaignInfluencerId,
        amount: partialAmount,
        remarks,
      });
      return;
    }

    if (amountType === AmountType.GST && !linkedInvoiceId) {
      ToastService.error("Please select a linked invoice");
      return;
    }

    createManualPayoutBank({
      campaignInfluencerId,
      amount: partialAmount,
      remarks,
      amountType,
      linkedInvoiceId,
    });
  };

  return (
    <Flex vertical gap={4}>
      {type !== "full" && (
        <Space>
          <Input
            placeholder="Remarks"
            value={remarks}
            status={hasInvalidRemarks ? "error" : undefined}
            onChange={(e) => setRemarks(e.target.value)}
          />
          {type === "manual" && (
            <>
              <Select
                options={[
                  { label: "Base", value: AmountType.Base },
                  { label: "GST", value: AmountType.GST },
                ]}
                value={amountType}
                onChange={(value) => {
                  setAmountType(value);
                  setLinkedInvoiceId("");
                }}
              />
              {amountType === AmountType.GST && (
                <Select
                  prefix={<h4> Linked Invoice: </h4>}
                  style={{ width: "100%", minWidth: "200px" }}
                  placeholder="Select Linked Invoice"
                  options={invoices.map((invoice) => ({
                    label: invoice.remarks,
                    value: invoice._id,
                  }))}
                  value={linkedInvoiceId}
                  onChange={(value) => setLinkedInvoiceId(value)}
                />
              )}
            </>
          )}
          <InputNumber
            addonBefore={"Amount ₹"}
            className={styles.amountInput}
            value={partialAmount}
            onChange={(value) => {
              setpartialAmount(value ?? 0);
            }}
            min={0}
            status={exceedsMaxPayable ? "error" : undefined}
          />
        </Space>
      )}
      {remarksWarning ? (
        <p className={styles.amountError}>{remarksWarning}</p>
      ) : null}
      {exceedsMaxPayable ? (
        <p className={styles.amountError}>
          Amount cannot exceed max payable ₹{fullAmountByType.toFixed(2)}
        </p>
      ) : null}
      <p>
        Are you sure you want to pay the influencer/agency the full amount of ₹
        (<strong>{amount.toFixed(2)}</strong>)
      </p>
      <p>
        The payout link will be sent to the following
        <br />{" "}
        <strong>
          {isPrimaryEmail
            ? `Email: ${primaryContactText}`
            : `Phone Number: ${primaryContactText}`}
        </strong>
      </p>
      <Flex justify="end" gap={8}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isCreating || isCreatingManualPayoutBank}
          disabled={
            exceedsMaxPayable ||
            hasInvalidRemarks ||
            (amountType === AmountType.GST && !linkedInvoiceId)
          }
        >
          Confirm
        </Button>
      </Flex>
    </Flex>
  );
}
